import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY;
const oauthClientId = process.env.REACT_APP_SUPABASE_OAUTH_CLIENT_ID;
const oauthRedirectUri = process.env.REACT_APP_SUPABASE_OAUTH_REDIRECT_URI;

const getRedirectUri = () => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const fallback = origin ? `${origin}/oauth/callback` : '';
  if (!oauthRedirectUri) return fallback;
  try {
    const envUrl = new URL(oauthRedirectUri);
    if (origin && envUrl.origin === origin) return oauthRedirectUri;
  } catch {
    return fallback || oauthRedirectUri;
  }
  return fallback || oauthRedirectUri;
};

const getBackendBaseUrl = () => {
  const raw = process.env.REACT_APP_BACKEND_URL || '';
  const base = String(raw).replace(/\/$/, '');
  if (!base) return typeof window !== 'undefined' ? window.location.origin : '';
  try {
    const url = new URL(base);
    const isLocalHost = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
    const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
    const isCurrentLocalHost = currentHost === 'localhost' || currentHost === '127.0.0.1';
    if (isLocalHost && currentHost && !isCurrentLocalHost) {
      url.hostname = currentHost;
    }
    return url.toString().replace(/\/$/, '');
  } catch {
    return base;
  }
};

const OAuthCallback = () => {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const code = params.get('code') || '';
  const errorParam = params.get('error') || '';
  const errorDescription = params.get('error_description') || '';
  const [loading, setLoading] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [error, setError] = useState('');
  const [stored, setStored] = useState(false);
  const [storeError, setStoreError] = useState('');

  useEffect(() => {
    const run = async () => {
      if (errorParam) {
        setError(`${errorParam}${errorDescription ? `: ${errorDescription}` : ''}`);
        return;
      }
      if (!code) {
        setError('code es requerido');
        return;
      }
      if (!supabaseUrl || !supabaseKey) {
        setError('Supabase no está configurado en este entorno.');
        return;
      }
      if (!oauthClientId) {
        setError('Falta REACT_APP_SUPABASE_OAUTH_CLIENT_ID en el entorno.');
        return;
      }
      const verifier = sessionStorage.getItem('supabase_oauth_code_verifier') || '';
      if (!verifier) {
        try {
          const retryKey = 'supabase_oauth_retry';
          const retried = sessionStorage.getItem(retryKey);
          if (!retried) {
            sessionStorage.setItem(retryKey, '1');
            window.location.assign('/oauth/consent');
            return;
          }
        } catch {
          setError('No se encontró code_verifier en sessionStorage.');
          return;
        }
        setError('No se encontró code_verifier en sessionStorage.');
        return;
      }
      setLoading(true);
      setError('');
      const redirectUri = getRedirectUri();
      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: oauthClientId,
        code_verifier: verifier,
        redirect_uri: redirectUri,
        code,
      });
      const response = await fetch(`${supabaseUrl.replace(/\/$/, '')}/auth/v1/oauth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          apikey: supabaseKey,
        },
        body,
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        setError(data?.error_description || data?.msg || 'Error al intercambiar token.');
        setLoading(false);
        return;
      }
      sessionStorage.removeItem('supabase_oauth_code_verifier');
      sessionStorage.removeItem('supabase_oauth_retry');
      setTokenData(data);
      setStoreError('');
      const backendBase = getBackendBaseUrl();
      if (backendBase) {
        try {
          if (typeof window !== 'undefined') {
            window.localStorage?.setItem('ai_chat_api_base', backendBase);
            // Fallback para Safari: guardar tokens en sessionStorage
            if (data?.access_token) sessionStorage.setItem('mcp_access_token', String(data.access_token));
            if (data?.refresh_token) sessionStorage.setItem('mcp_refresh_token', String(data.refresh_token));
          }
        } catch {
          // ignore storage errors
        }
        const storeResp = await fetch(`${backendBase}/api/ai-chat/token`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            access_token: data?.access_token || '',
            refresh_token: data?.refresh_token || '',
            expires_in: data?.expires_in ?? null,
          }),
        });
        const storeData = await storeResp.json().catch(() => null);
        if (!storeResp.ok) {
          setStoreError(storeData?.error || 'No se pudo guardar el token en el backend.');
        } else {
          setStored(true);
        }
      }
      setLoading(false);
    };
    run();
  }, [code, errorParam, errorDescription]);

  useEffect(() => {
    if (!stored) return;
    try {
      const returnTo = sessionStorage.getItem('ai_chat_return_to');
      if (!returnTo) return;
      sessionStorage.removeItem('ai_chat_return_to');
      window.location.assign(returnTo);
    } catch {
      return;
    }
  }, [stored]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-2xl space-y-4">
        <h1 className="text-2xl font-semibold">OAuth Callback</h1>
        {error ? (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        ) : null}
        {loading ? <div className="text-sm text-slate-600">Intercambiando token...</div> : null}
        {storeError ? (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{storeError}</div>
        ) : null}
        {stored ? (
          <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
            Token guardado en el backend.
          </div>
        ) : null}
        {tokenData ? (
          <div className="space-y-3 rounded border border-slate-200 p-4">
            <div className="text-sm">
              <span className="font-medium">token_type:</span> {tokenData?.token_type || ''}
            </div>
            <div className="text-sm">
              <span className="font-medium">expires_in:</span> {tokenData?.expires_in || ''}
            </div>
            <div className="text-sm">
              <div className="font-medium">access_token</div>
              <textarea
                className="mt-1 w-full rounded border border-slate-300 p-2 text-xs"
                rows={6}
                value={tokenData?.access_token || ''}
                readOnly
              />
            </div>
            <div className="text-sm">
              <div className="font-medium">refresh_token</div>
              <textarea
                className="mt-1 w-full rounded border border-slate-300 p-2 text-xs"
                rows={4}
                value={tokenData?.refresh_token || ''}
                readOnly
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default OAuthCallback;
